import { supabase } from './supabaseClient';

export const adminService = {
  async getPendingRegistrations() {
    const { data, error } = await supabase
      .from('registration_queue')
      .select('*')
      .eq('status', 'pending')
      .order('submitted_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async approveRegistration(registrationId /* , registrationData (unused) */) {
    // Calls the SECURITY DEFINER `approve_registration` RPC, which atomically:
    //   1. Verifies caller is Admin
    //   2. Looks up auth.users.id by email (admins can't read auth.users
    //      directly)
    //   3. Inserts (or upserts on retry) the public.users row with that id
    //   4. Marks registration_queue.status = 'approved'
    //   5. Writes the admin_logs entry
    // The old client-side approach did an UPDATE WHERE email=… which silently
    // affected zero rows because signup never created a public.users row —
    // admins were hand-creating users after clicking Approve. The RPC closes
    // that gap. See migration 20260527000000_approve_registration_rpc.sql.
    const { data, error } = await supabase.rpc('approve_registration', {
      registration_id: registrationId,
    });
    if (error) throw error;
    return data;
  },

  async rejectRegistration(registrationId, reason) {
    const { error } = await supabase
      .from('registration_queue')
      .update({
        status: 'rejected',
        rejection_reason: reason,
        reviewed_at: new Date(),
      })
      .eq('id', registrationId);

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_logs')
      .insert([
        {
          action: 'registration_rejected',
          details: { reason },
        },
      ]);
  },

  async getAllUsers() {
    const { data, error } = await supabase
      .from('users')
      .select(`
        id,
        email,
        name,
        position,
        country_id,
        status,
        created_at,
        role_id,
        team_lead_id,
        roles(id, name)
      `)
      .order('created_at', { ascending: false });
    if (error) throw error;

    // Flatten the response to include role name
    return data.map(user => ({
      ...user,
      role: user.roles?.name || 'Unknown'
    }));
  },

  async deleteUser(userId) {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', userId);
    if (error) throw error;
  },

  async resetPassword(userId) {
    const { error } = await supabase.auth.admin.updateUserById(userId, {
      password: Math.random().toString(36).slice(-12),
    });
    if (error) throw error;
  },

  async updateUser(userId, { name, email, position, role, team_lead_id }) {
    // Look up the role_id based on role name
    const { data: roleData, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', role)
      .single();

    if (roleError) throw roleError;

    const { error } = await supabase
      .from('users')
      .update({
        name,
        email,
        position,
        role_id: roleData.id,
        team_lead_id: team_lead_id || null,
      })
      .eq('id', userId);

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_logs')
      .insert([
        {
          action: 'user_updated',
          target_user_id: userId,
          details: { name, email, position, role, team_lead_id },
        },
      ]);
  },

  async deactivateUser(userId) {
    const { error } = await supabase
      .from('users')
      .update({ status: 'inactive' })
      .eq('id', userId);

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_logs')
      .insert([
        {
          action: 'user_deactivated',
          target_user_id: userId,
        },
      ]);
  },

  async reactivateUser(userId) {
    const { error } = await supabase
      .from('users')
      .update({ status: 'active' })
      .eq('id', userId);

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_logs')
      .insert([
        {
          action: 'user_reactivated',
          target_user_id: userId,
        },
      ]);
  },

  async promoteToAdmin(userId) {
    // Get Admin role ID
    const { data: adminRole, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'Admin')
      .single();

    if (roleError) throw roleError;

    const { error } = await supabase
      .from('users')
      .update({ role_id: adminRole.id })
      .eq('id', userId);

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_logs')
      .insert([
        {
          action: 'user_promoted',
          target_user_id: userId,
          details: { promoted_to: 'Admin' },
        },
      ]);
  },

  async promoteToManager(userId) {
    // Get Manager role ID
    const { data: managerRole, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'Manager')
      .single();

    if (roleError) throw roleError;

    const { error } = await supabase
      .from('users')
      .update({ role_id: managerRole.id })
      .eq('id', userId);

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_logs')
      .insert([
        {
          action: 'user_promoted',
          target_user_id: userId,
          details: { promoted_to: 'Manager' },
        },
      ]);
  },

  async demoteUser(userId, newRole) {
    // Get role ID for the new role
    const { data: roleData, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', newRole)
      .single();

    if (roleError) throw roleError;

    const { error } = await supabase
      .from('users')
      .update({ role_id: roleData.id })
      .eq('id', userId);

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_logs')
      .insert([
        {
          action: 'user_demoted',
          target_user_id: userId,
          details: { demoted_to: newRole },
        },
      ]);
  },

  /**
   * Assign role to user
   * @param {string} userId - User's UUID
   * @param {string} roleId - Role's UUID
   * @returns {Promise<void>}
   */
  async assignUserRole(userId, roleId) {
    try {
      const { error } = await supabase
        .from('users')
        .update({ role_id: roleId })
        .eq('id', userId);

      if (error) throw error;
    } catch (err) {
      console.error('Error assigning role:', err);
      throw err;
    }
  },

  /**
   * Assign multiple countries to user
   * @param {string} userId - User's UUID
   * @param {Array<string>} countryIds - Array of Country UUIDs
   * @param {string} assignedBy - Admin/Manager's UUID making the assignment
   * @returns {Promise<void>}
   */
  async assignCountries(userId, countryIds, assignedBy) {
    try {
      // Delete existing country assignments
      await supabase
        .from('user_countries')
        .delete()
        .eq('user_id', userId);

      // Insert new country assignments
      if (countryIds.length > 0) {
        const { error } = await supabase
          .from('user_countries')
          .insert(
            countryIds.map(countryId => ({
              user_id: userId,
              country_id: countryId,
              assigned_by: assignedBy,
            }))
          );

        if (error) throw error;
      }
    } catch (err) {
      console.error('Error assigning countries:', err);
      throw err;
    }
  },

  /**
   * Get all countries
   * @returns {Promise<Array<object>>}
   */
  async getAllCountries() {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('id, name, code')
        .order('name');

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching countries:', err);
      return [];
    }
  },

  /**
   * Get user's assigned countries
   * @param {string} userId - User's UUID
   * @returns {Promise<Array<object>>}
   */
  async getUserCountries(userId) {
    try {
      const { data, error } = await supabase
        .from('user_countries')
        .select('countries(id, name, code)')
        .eq('user_id', userId);

      if (error) throw error;
      return data.map(uc => uc.countries);
    } catch (err) {
      console.error('Error fetching user countries:', err);
      return [];
    }
  },
};
